import { useQuery } from '@tanstack/react-query';
import { useNostr } from '@nostrify/react';
import { parseFundraiser, type Fundraiser } from '@/types/fundraiser';

export function useFundraisers(authorPubkey?: string) {
  const { nostr } = useNostr();

  return useQuery<Fundraiser[], Error>({
    queryKey: ['fundraisers', authorPubkey],
    staleTime: 30000,
    queryFn: async (c) => {
      const signal = AbortSignal.any([c.signal, AbortSignal.timeout(5000)]);

      const filter: Parameters<typeof nostr.query>[0][0] = {
        kinds: [9041], // NIP-75 Zap Goals
        limit: 100,
      };

      if (authorPubkey) {
        filter.authors = [authorPubkey];
      }

      const events = await nostr.query([filter], { signal });

      const fundraisers = events
        .map(parseFundraiser)
        .filter((f): f is Fundraiser => f !== null)
        .sort((a, b) => b.createdAt - a.createdAt);

      return fundraisers;
    },
  });
}

export function useFundraiser(eventId: string) {
  const { nostr } = useNostr();

  return useQuery<Fundraiser | null, Error>({
    queryKey: ['fundraiser', eventId],
    staleTime: 30000,
    queryFn: async (c) => {
      const signal = AbortSignal.any([c.signal, AbortSignal.timeout(5000)]);

      const events = await nostr.query([{
        kinds: [9041],
        ids: [eventId],
      }], { signal });

      if (events.length === 0) return null;

      return parseFundraiser(events[0]);
    },
  });
}
